import {
  LitElement,
  html,
} from "https://cdn.jsdelivr.net/gh/lit/dist@3/all/lit-all.min.js";

/**
 * "Ejects" from the shadow DOM.
 * Advantage: The element and it's children will be styled according to (cascading) global style selectors.
 * Disadvantage: slots won't work within these elements.
 */
class LitElementEjected extends LitElement {
  createRenderRoot() {
    // Returning `this` will render the template directly into the light DOM
    return this;
  }
}

export class SubscriptionWidget extends LitElementEjected {
  static properties = {
    productId: { type: String },
    variantId: { type: String },
    _product: { type: Object, state: true },
    _variants: { type: Array, state: true },
    _selectedVariant: { type: Object, state: true },
    _selectedOption: { type: String, state: true },
    _selectedPlan: { type: Object, state: true },
    _plans: { type: Array, state: true },
    _loadingState: { type: String, state: true },
    _quantity: { type: Number, state: true },
    _slashedPrice: { type: String, state: true },
    _actualPrice: { type: String, state: true },
  };

  constructor() {
    super();
    this.productId = null;
    this.variantId = null;
    this._product = null;
    this._variants = [];
    this._selectedVariant = null;
    this._selectedPlan = null;
    this._plans = [];
    this._loadingState = "idle";
    this._selectedOption = null;
    this._slashedPrice = undefined;
    this._actualPrice = undefined;
    this._quantity = 1;
  }

  connectedCallback() {
    super.connectedCallback();
    this._init();
  }

  updated(changedProps) {
    //update pricing when variant id changes
    if (changedProps.has("variantId")) {
      if (
        changedProps.get("variantId") !== undefined &&
        this._loadingState === "idle"
      ) {
        this._setupProductState();
      }
    }
  }

  async _init() {
    if (!this.productId || !this.variantId) {
      console.error("Product ID or Variant ID is missing");
      return;
    }

    this._loadingState = "loading";

    try {
      await this._fetchProductData();
      this._loadingState = "idle";
    } catch (error) {
      console.error("Error initializing widget:", error);
      this._loadingState = "error";
    }
  }

  async _fetchProductData() {
    const session = await recharge.auth.loginShopifyAppProxy();
    const settings = await recharge.cdn.getCDNProductAndSettings(
      this.productId
    );

    const product_settings = settings.product;
    const productQuery = await recharge.product.productSearch(session, {
      format_version: "2022-06",
      external_product_ids: [this.productId],
    });

    if (!productQuery.products || productQuery.products.length === 0) {
      throw new Error("Product not found");
    }

    this._product = productQuery.products[0];
    this._variants = product_settings.variants.filter(
      (v) => v.is_published !== false
    );

    this._setupProductState();
  }

  _setupProductState() {
    this._selectedVariant = this._variants.find(
      (v) => Number(v.id) === Number(this.variantId)
    );

    if (!this._selectedVariant) {
      throw new Error("Selected variant not found");
    }

    this._plans = this._product.plans
      .filter((plan) => plan.type !== "onetime")
      .sort((a, b) => a.sort_order - b.sort_order);

    this._selectedPlan = this._plans.length > 0 ? this._plans[0] : null;

    if (this._selectedPlan) {
      this._selectedOption = "subscriptions";
      this._updatePricing();
    } else {
      this._selectedOption = "one-time-purchase";
    }
  }

  _updatePricing() {
    if (!this._selectedVariant || !this._selectedPlan) return;

    for (const plan of this._plans) {
      if (!this._selectedVariant.selling_plan_allocations) continue;

      const allocation = this._selectedVariant.selling_plan_allocations.find(
        (price) =>
          price.selling_plan_id.toString() === plan.external_plan_id.toString()
      );

      if (allocation) {
        plan.actualPrice = `$${allocation.discounted_price}`;
        plan.slashedPrice = `$${this._selectedVariant.prices.unit_price}`;
      } else {
        plan.actualPrice = `$${this._selectedVariant.prices.unit_price}`;
      }
    }

    if (this._selectedOption === "subscriptions" && this._selectedPlan) {
      this._actualPrice = this._selectedPlan.actualPrice;
      this._slashedPrice = this._selectedPlan.slashedPrice;
    } else {
      this._actualPrice = `$${this._selectedVariant.prices.unit_price}`;
      this._slashedPrice = undefined;
    }
  }

  _handleRadioChange(event) {
    this._selectedOption = event.target.value;
    this.requestUpdate();
  }

  _updateFrequency(event) {
    const planId = Number(event.target.value);
    this._selectedPlan = this._plans.find((p) => Number(p.id) === planId);
    this.requestUpdate();
  }

  _renderFrequencyOption(plan) {
    if (
      plan.external_variant_ids.length > 0 &&
      !plan.external_variant_ids.find(
        (id) => Number(id.ecommerce) === Number(this.variant_id)
      )
    )
      return;

    return html`
      <option
        value="${plan.external_plan_id}"
        data-interval="${plan.subscription_preferences
          .order_interval_frequency}"
        ${this._selectedPlan && this._selectedPlan.id === plan.id
          ? html`selected`
          : ""}
      >
        ${plan.title.replace("Onetime", "One-time")}
      </option>
    `;
  }

  render() {
    if (this._loadingState === "loading") {
      return html`<div class="sms-recharge-widget__loading">Loading...</div>`;
    }

    if (
      this._loadingState === "error" ||
      !this._product ||
      !this._selectedVariant
    ) {
      return html`<div class="sms-recharge-widget__error">
        Unable to load subscription options
      </div>`;
    }

    const showSubscriptionOption = this._plans && this._plans.length > 0;

    return html`
      <div class="sms-recharge-widget">
        ${showSubscriptionOption
          ? html`
              <div
                class="sms-recharge-widget__radio-option ${this
                  ._selectedOption === "subscriptions"
                  ? "sms-rw__radio-selected"
                  : "sms-rw__radio-unselected"}"
              >
                <div class="sms-rw__radio-label-container">
                  <label class="sms-rw__radio-label-left">
                    <input
                      type="radio"
                      id="${this.productId}-subscriptions"
                      name="purchase-option-${this.productId}"
                      value="subscriptions"
                      @change=${this._handleRadioChange}
                      ?checked=${this._selectedOption === "subscriptions"}
                      class="sms-rw__radio-label-input"
                    />
                    <span class="sms-rw__radio-label-text"
                      >Subscribe and Save (10%)</span
                    >
                  </label>

                  <div class="sms-rw__actual-price">${this._actualPrice}</div>
                </div>
                <div class="sms-widget__select-wrapper">
                  <select
                    class="sms-widget__select"
                    name="selling_plan"
                    id="selling_plan-${this.productId}"
                    @change=${this._updateFrequency}
                  >
                    ${this._plans.map((plan) =>
                      this._renderFrequencyOption(plan)
                    )}
                  </select>
                </div>
              </div>
            `
          : ""}

        <div
          class="sms-recharge-widget__radio-option ${this._selectedOption ===
          "one-time-purchase"
            ? "sms-rw__radio-selected"
            : "sms-rw__radio-unselected"}"
        >
          <div class="sms-rw__radio-label-container">
            <label class="sms-rw__radio-label-left">
              <input
                type="radio"
                id="${this.productId}-one-time-purchase"
                name="purchase-option-${this.productId}"
                value="one-time-purchase"
                @change=${this._handleRadioChange}
                ?checked=${this._selectedOption === "one-time-purchase"}
                class="sms-rw__radio-label-input"
              />
              <span class="sms-rw__radio-label-text">One-time purchase</span>
            </label>

            <div class="sms-rw__actual-price">
              $${this._selectedVariant.prices.unit_price}
            </div>
          </div>
        </div>

        ${this._selectedOption === "subscriptions"
          ? html`
              <div class="sms-rw-subscription-policy">
                <div class="sms-rw-subscription-policy-text-header">
                  Subscribe and Save (10%)
                </div>
                <div class="sms-rw-subscription-policy-text-main">
                  You're subscribing to receive this item multiple times, on a
                  recurring basis (according to the frequency you select) with a
                  discount on every recurring order. You may cancel or change
                  your subscription at any time.
                </div>
              </div>
            `
          : null}
      </div>
    `;
  }
}

customElements.define("sms-recharge-widget", SubscriptionWidget);
